import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { queryKeys, STALE_TIMES } from '@/lib/queryKeys';

interface OnboardingState {
  id?: string;
  currentStep: number;
  completed: boolean;
  stepsCompleted: string[];
}

const EMPTY_STATE: OnboardingState = {
  currentStep: 0,
  completed: false,
  stepsCompleted: [],
};

/**
 * Loads the current user's onboarding row.
 *
 * Multiple components on the dashboard (OnboardingTour, OnboardingCoordinator,
 * GuidedOnboarding, DashboardOnboardingTour, FloatingNavMenu) all call this
 * hook simultaneously. By routing through React Query with a shared key,
 * the request is deduped to a single round-trip per user, and subsequent
 * mounts within `staleTime` reuse the cached value.
 */
export const useOnboarding = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.userOnboarding(user?.id);

  const { data: onboarding = EMPTY_STATE, isLoading } = useQuery({
    queryKey,
    enabled: !!user,
    staleTime: STALE_TIMES.userOnboarding,
    gcTime: STALE_TIMES.userOnboarding * 2,
    queryFn: async (): Promise<OnboardingState> => {
      const { data, error } = await supabase
        .from('user_onboarding')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading onboarding:', error);
        return EMPTY_STATE;
      }

      if (!data) return EMPTY_STATE;

      return {
        id: data.id,
        currentStep: data.current_step,
        completed: data.completed,
        stepsCompleted: (data.steps_completed as string[]) ?? [],
      };
    },
  });

  // Optimistic local update + cache write so callers reflect changes
  // immediately without waiting for a refetch.
  const writeCache = useCallback(
    (next: OnboardingState) => {
      queryClient.setQueryData<OnboardingState>(queryKey, next);
    },
    [queryClient, queryKey],
  );

  const completeStep = useCallback(
    async (stepId: string) => {
      if (!user) return;

      const newStepsCompleted = [...onboarding.stepsCompleted, stepId];
      const newCurrentStep = onboarding.currentStep + 1;

      try {
        if (onboarding.id) {
          await supabase
            .from('user_onboarding')
            .update({
              current_step: newCurrentStep,
              steps_completed: newStepsCompleted,
            })
            .eq('id', onboarding.id);

          writeCache({
            ...onboarding,
            currentStep: newCurrentStep,
            stepsCompleted: newStepsCompleted,
          });
        } else {
          const { data } = await supabase
            .from('user_onboarding')
            .insert({
              user_id: user.id,
              current_step: newCurrentStep,
              steps_completed: newStepsCompleted,
            })
            .select()
            .single();

          if (data) {
            writeCache({
              id: data.id,
              currentStep: newCurrentStep,
              completed: false,
              stepsCompleted: newStepsCompleted,
            });
          }
        }
      } catch (error) {
        console.error('Error completing step:', error);
        toast.error('Failed to save progress');
      }
    },
    [user, onboarding, writeCache],
  );

  const completeOnboarding = useCallback(async () => {
    if (!user || !onboarding.id) return;

    try {
      await supabase
        .from('user_onboarding')
        .update({
          completed: true,
          completed_at: new Date().toISOString(),
        })
        .eq('id', onboarding.id);

      writeCache({ ...onboarding, completed: true });
      toast.success('Welcome aboard! 🎉');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete onboarding');
    }
  }, [user, onboarding, writeCache]);

  const resetOnboarding = useCallback(async () => {
    if (!user || !onboarding.id) return;

    try {
      await supabase
        .from('user_onboarding')
        .update({
          current_step: 0,
          completed: false,
          completed_at: null,
          steps_completed: [],
        })
        .eq('id', onboarding.id);

      writeCache({
        id: onboarding.id,
        currentStep: 0,
        completed: false,
        stepsCompleted: [],
      });
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    }
  }, [user, onboarding, writeCache]);

  return {
    onboarding,
    loading: isLoading,
    completeStep,
    completeOnboarding,
    resetOnboarding,
  };
};
